import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_BASE_URL } from '../config/api.config';
import { AdminDashboardStats } from '../models/admin-stats.models';
import { ListResponse } from '../models/list-response.model';

@Injectable({ providedIn: 'root' })
export class AdminStatsService {
  private readonly http = inject(HttpClient);

  getDashboardStats(): Observable<AdminDashboardStats> {
    return this.http.get<AdminDashboardStats>(`${API_BASE_URL}/v1/management/stats`).pipe(
      catchError((error) => {
        if (error?.status !== 404) {
          return throwError(() => error);
        }
        return this.getDashboardStatsFallback();
      })
    );
  }

  private getDashboardStatsFallback(): Observable<AdminDashboardStats> {
    const recipeStatuses = ['DRAFT', 'REVIEW', 'APPROVED', 'DENIED', 'DELETED', 'UPDATED'] as const;
    const inspirationStatuses = ['DRAFT', 'REVIEW', 'APPROVED', 'DENIED', 'DELETED', 'UPDATED'] as const;

    const basePageParams = new HttpParams()
      .set('page', '0')
      .set('size', '1');

    const usersCount$ = this.getTotal(`${API_BASE_URL}/users/admin/search`, basePageParams);
    const categoriesCount$ = this.getTotal(`${API_BASE_URL}/categories/0/1`);
    const recipesByStatus$ = forkJoin(
      recipeStatuses.map((status) =>
        this.getTotal(
          `${API_BASE_URL}/v1/management/moderation/recipes`,
          basePageParams.set('status', status)
        )
      )
    );
    const inspirationsByStatus$ = forkJoin(
      inspirationStatuses.map((status) =>
        this.getTotal(
          `${API_BASE_URL}/v1/management/moderation/inspirations`,
          basePageParams.set('status', status)
        )
      )
    );
    const reviewMessagesInReviewCount$ = this.getTotal(
      `${API_BASE_URL}/v1/management/moderation/reviews/messages`,
      basePageParams
    );
    const openReportsCount$ = this.getTotal(
      `${API_BASE_URL}/v1/admin/reports`,
      basePageParams.set('status', 'OPEN')
    );

    return forkJoin({
      usersCount: usersCount$,
      categoriesCount: categoriesCount$,
      recipesByStatus: recipesByStatus$,
      inspirationsByStatus: inspirationsByStatus$,
      reviewMessagesInReviewCount: reviewMessagesInReviewCount$,
      openReportsCount: openReportsCount$
    }).pipe(
      map((result) => {
        const recipeStatusTotal = result.recipesByStatus.reduce((sum, value) => sum + value, 0);
        const inspirationStatusTotal = result.inspirationsByStatus.reduce((sum, value) => sum + value, 0);
        const recipeReviewIndex = recipeStatuses.indexOf('REVIEW');
        const recipeApprovedIndex = recipeStatuses.indexOf('APPROVED');
        const inspirationReviewIndex = inspirationStatuses.indexOf('REVIEW');

        return {
          usersCount: result.usersCount,
          // Fallback mode: no endpoint currently exposes enabled users count directly.
          enabledUsersCount: result.usersCount,
          // Fallback mode: no endpoint currently exposes anonymous users count directly.
          anonymousUsersCount: 0,
          categoriesCount: result.categoriesCount,
          recipesCount: recipeStatusTotal,
          approvedRecipesCount: result.recipesByStatus[recipeApprovedIndex] ?? 0,
          recipesInReviewCount: result.recipesByStatus[recipeReviewIndex] ?? 0,
          inspirationsCount: inspirationStatusTotal,
          inspirationsInReviewCount: result.inspirationsByStatus[inspirationReviewIndex] ?? 0,
          reviewMessagesInReviewCount: result.reviewMessagesInReviewCount,
          openReportsCount: result.openReportsCount
        };
      })
    );
  }

  private getTotal(url: string, params?: HttpParams): Observable<number> {
    return this.http.get<ListResponse<unknown>>(url, { params }).pipe(
      map((response) => Number(response?.total ?? 0)),
      catchError(() => of(0))
    );
  }
}
